import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/reports`, {
        report_type: "contact",
        subject: formData.subject,
        description: `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`,
        contact_email: formData.email
      });
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>
            <Link to="/" className="text-rose-600 hover:text-rose-700 font-medium">
              ← {t('nav.backToHome')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-gradient-to-r from-rose-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4">{t('contact.title')}</h1>
          <p className="text-rose-100 text-lg">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="font-serif text-2xl text-rose-900 mb-6">{t('contact.getInTouch')}</h2>
                <p className="text-rose-700 leading-relaxed">
                  {t('contact.getInTouchDesc')}
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-light text-rose-500">01</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-rose-900 mb-1">{t('contact.emailUs')}</h3>
                      <p className="text-rose-600 text-sm mb-2">{t('contact.emailUsDesc')}</p>
                      <a href="mailto:Matchmaker@singleseen.com" className="text-rose-500 font-medium hover:text-rose-600" data-testid="contact-email">
                        Matchmaker@singleseen.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-light text-emerald-500">02</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-rose-900 mb-1">{t('contact.liveChatSupport')}</h3>
                      <p className="text-rose-600 text-sm mb-2">{t('contact.liveChatDesc')}</p>
                      <p className="text-emerald-600 font-medium flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                        {t('contact.available247')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-light text-amber-600">03</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-rose-900 mb-1">{t('contact.responseTime')}</h3>
                      <p className="text-rose-600 text-sm mb-2">{t('contact.responseTimeDesc')}</p>
                      <p className="text-amber-600 font-medium">{t('contact.responseTimeValue')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-light text-purple-500">04</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-rose-900 mb-1">{t('contact.location')}</h3>
                      <p className="text-rose-600 text-sm mb-2">{t('contact.locationDesc')}</p>
                      <p className="text-purple-600 font-medium">{t('contact.locationValue')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Link */}
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-6 border border-rose-100">
                <h3 className="font-semibold text-rose-900 mb-2">{t('contact.faqLink')}</h3>
                <p className="text-rose-600 text-sm mb-4">
                  {t('contact.faqLinkDesc')}
                </p>
                <Link to="/help" className="text-rose-500 font-medium hover:text-rose-600">
                  {t('contact.visitHelp')} →
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 border border-rose-100 shadow-lg">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl text-emerald-500">✓</span>
                  </div>
                  <h3 className="font-serif text-2xl text-rose-900 mb-3">{t('contact.messageSent')}</h3>
                  <p className="text-rose-600 mb-6">
                    {t('contact.messageSentDesc')}
                  </p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", email: "", subject: "", message: "" });
                    }}
                    variant="outline"
                    className="border-rose-300 text-rose-700"
                  >
                    {t('contact.sendAnother')}
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-2xl text-rose-900 mb-6">{t('contact.formTitle')}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-medium text-rose-700 mb-2">{t('contact.nameLabel')}</label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder={t('contact.namePlaceholder')}
                        required
                        className="border-rose-200 focus:border-rose-500 focus:ring-rose-500"
                        data-testid="contact-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-rose-700 mb-2">{t('contact.emailLabel')}</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="your.email@example.com"
                        required
                        className="border-rose-200 focus:border-rose-500 focus:ring-rose-500"
                        data-testid="contact-email-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-rose-700 mb-2">{t('contact.subjectLabel')}</label>
                      <Input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        placeholder="What is this about?"
                        required
                        className="border-rose-200 focus:border-rose-500 focus:ring-rose-500"
                        data-testid="contact-subject-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-rose-700 mb-2">{t('contact.messageLabel')}</label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Tell us how we can help you..."
                        rows={5}
                        required
                        className="border-rose-200 focus:border-rose-500 focus:ring-rose-500"
                        data-testid="contact-message-input"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full py-6"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></span>
                          {t('common.sending')}
                        </span>
                      ) : (
                        t('common.sendMessage')
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-rose-900 text-rose-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SingleSeen" className="h-10 w-auto brightness-0 invert opacity-80" />
            </div>
            <div className="flex gap-8 text-sm">
              <Link to="/love-stories" className="hover:text-white transition-colors">Love Stories</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
            <p className="text-sm">© 2025 SingleSeen. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
